import { motion } from "framer-motion";
import { Trophy, Award, Sparkles } from "lucide-react";

// Awards section — currently anchored by the 2026 Master Agent Award –
// Growth Champion from Total Wireless (Verizon Value). Structured so
// adding future awards is a one-line addition to the AWARDS array.
// Rendered on Home, About, Become-a-Dealer, and Total Wireless Master
// Agent pages so the recognition is visible on every high-intent page.
const AWARDS = [
  {
    year: "2026",
    title: "Master Agent Award — Growth Champion",
    issuer: "Total Wireless (Verizon Value)",
    subtitle: "#1 in growth among Total Wireless Master Agents",
    body:
      "Awarded to Prime Media Wireless in recognition of significant nationwide expansion, aggressive field-support investment, and industry-leading pace onboarding multi-store operators onto the Total Wireless network.",
    icon: Trophy,
    accent: "teal",
    testid: "award-2026-growth-champion",
  },
];

export function AwardsSection({
  heading = "Recognized as the #1 Growth Master Agent",
  subheading = "Awards and industry recognition earned by Prime Media Wireless.",
  className = "",
  compact = false,
}) {
  return (
    <section
      className={`${compact ? "py-12" : "py-16 md:py-20"} bg-slate-50 ${className}`}
      data-testid="awards-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10">
          <p className="label-style text-teal mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Awards & Recognition
          </p>
          <h2 className="font-heading font-bold text-navy text-3xl md:text-4xl mb-3">
            {heading}
          </h2>
          {subheading && (
            <p className="text-slate-600 font-body text-base md:text-lg">
              {subheading}
            </p>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-1">
          {AWARDS.map((a, idx) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.testid}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: idx * 0.05, duration: 0.35 }}
                className="relative overflow-hidden rounded-2xl border border-teal/40 bg-gradient-to-br from-navy to-navy-dark text-white shadow-xl"
                data-testid={a.testid}
              >
                {/* Decorative flourish */}
                <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-teal/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-teal/10 blur-3xl" />

                <div className="relative p-8 md:p-10 flex flex-col md:flex-row items-start gap-6">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-teal text-navy flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Icon className="w-10 h-10 md:w-12 md:h-12" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="label-style text-[10px] px-2 py-0.5 rounded-full bg-teal text-navy font-semibold">
                        {a.year}
                      </span>
                      <span className="label-style text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/80">
                        {a.issuer}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-2xl md:text-3xl mb-2">
                      {a.title}
                    </h3>
                    <p className="text-teal font-heading font-semibold text-base md:text-lg mb-3">
                      {a.subtitle}
                    </p>
                    <p className="text-slate-200 font-body text-sm md:text-base leading-relaxed max-w-3xl">
                      {a.body}
                    </p>
                  </div>
                  <Award
                    className="hidden md:block w-24 h-24 text-teal/20 absolute right-8 top-8"
                    strokeWidth={1.5}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default AwardsSection;
